<script setup>
import { onMounted, computed, ref, watch, nextTick, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";

import SlideItem from "./SliderItem.vue";

const { proxy } = getCurrentInstance();

// i18n 초기화
const { t, locale } = useI18n();

// Swiper 인스턴스 참조
const swiperInstance = ref(null);

// Swiper 초기화 함수
const initSwiper = () => {
  if (!window.Swiper) return;
  
  // 기존 Swiper 인스턴스가 있다면 제거
  if (swiperInstance.value) {
    swiperInstance.value.destroy();
  }

  swiperInstance.value = new window.Swiper(".swiper-container", {
    loop: true,
    autoplay: {
      delay: 4000,
    },
    speed: 1500,
    slidesPerView: 1,
    spaceBetween: 0,
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
  });
};

// 언어 변경 감지
watch(() => locale.value, () => {
  // 다음 틱에서 Swiper 재초기화
  nextTick(() => {
    initSwiper();
  });
});

onMounted(() => {
  const id = setInterval(() => {
    if (!window.Swiper) {
      return;
    }
    clearInterval(id);
    initSwiper();
  }, 100);
});

// 다국어 지원을 위한 slides 데이터 계산
const slides = computed(() => [
  {
    image: "/assets/img/slider/1.jpg",
    subtitle: t('slider.casino.subtitle'),
    titles: [t('slider.casino.title1'), t('slider.casino.title2')],
    buttonIcon: "bi-play",
    playUrl: "#casino-section",
  },
  {
    image: "/assets/img/slider/2.jpg",
    subtitle: t('slider.slot.subtitle'),
    titles: [t('slider.slot.title1'), t('slider.slot.title2')],
    buttonIcon: "bi-plus",
    playUrl: "#slotgame-section",
  },
  ...(
    proxy.$store.state.settings.isSportsOn
      ? [
        {
          image: "/assets/img/slider/3.jpg",
          subtitle: t('slider.sports.subtitle'),
          titles: [t('slider.sports.title1'), t('slider.sports.title2')],
          buttonIcon: "bi-plus",
          playUrl: "#sports",
        },
      ]
      : []
  ),
]);
</script>

<template>
  <section class="wptb-slider p-0">
    <div
      class="swiper-container swiper-main-slider swiper-initialized swiper-horizontal swiper-pointer-events swiper-autoheight swiper-backface-hidden"
    >
      <!-- Swiper Slides -->
      <div
        class="swiper-wrapper"
        id="swiper-wrapper"
        aria-live="polite"
      >
        <SlideItem
          v-for="(slide, index) in slides"
          :key="index"
          :index="index"
          :image="slide.image"
          :subtitle="slide.subtitle"
          :titles="slide.titles"
          :button-icon="slide.buttonIcon"
          :play-url="slide.playUrl"
        />
      </div>

      <!-- Pagination Dots -->
      <div class="wptb-swiper-dots">
        <div
          class="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal"
        >
          <span
            v-for="(dot, index) in slides.length"
            :key="index"
            class="swiper-pagination-bullet"
            :class="{ 'swiper-pagination-bullet-active': index === 0 }"
            tabindex="0"
            role="button"
            :aria-label="'Go to slide ' + (index + 1)"
            :aria-current="index === 0 ? 'true' : null"
          ></span>
        </div>
      </div>
      <!-- Notification -->
      <span
        class="swiper-notification"
        aria-live="assertive"
        aria-atomic="true"
      ></span>
    </div>
  </section>
</template>

<style scoped>
.wptb-slider {
  position: relative;
  width: 100%;
}

.swiper-container {
  height: 100vh;
}

/* 태블릿 디바이스 */
@media (max-width: 1024px) {
  .swiper-container {
    height: 80vh;
  }
}

/* 모바일 디바이스 */
@media (max-width: 768px) {
  .swiper-container {
    height: 60vh;
  }
}

/* 작은 모바일 디바이스 */
@media (max-width: 480px) {
  .swiper-container {
    height: 50vh;
  }
}

.wptb-swiper-dots {
  position: absolute;
  bottom: 20px;
  width: 100%;
  text-align: center;
  z-index: 1;
}

@media (max-width: 768px) {
  .wptb-swiper-dots {
    display: none;
  }
}
</style>
